/**
 * AbapHeatmapPanel — SAP ABAP Custom-Code Heat Map (Iter 29).
 *
 * Two-step experience:
 *   1. On mount, fetches the deterministic rollup from GET /sap-ecc/heatmap/data
 *      and renders a per-package risk-banded table immediately. Works even
 *      without an LLM key.
 *   2. The "Generate AI report" button calls POST /sap-ecc/heatmap/generate
 *      which queues a Claude Sonnet 4.5 deliverable. We redirect the user
 *      to /deliverables?open={id} where the existing Deliverables UI
 *      handles the polling + markdown rendering.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FireIcon as Fire,
  CircleNotchIcon as Spinner,
  WarningOctagonIcon as WarningOctagon,
  SparkleIcon as Sparkle,
  ArrowSquareOutIcon as ArrowSquareOut,
  ChartBarIcon as ChartBar,
  DownloadSimpleIcon as Download,
  ShareNetworkIcon as ShareNetwork,
} from "@phosphor-icons/react";
import { abapHeatmapPdfUrl, generateAbapHeatmap, getAbapHeatmapData } from "@/lib/api";
import HeatmapShareModal from "./HeatmapShareModal";
import HeatmapSharesList from "./HeatmapSharesList";
import { formatApiErrorDetail } from "@/lib/auth";

// Carbon-aligned colour tokens per risk band.
const BAND_STYLE = {
  HIGH:   { bg: "#fff1f1", fg: "#a2191f", border: "#ffd7d9", label: "HIGH" },
  MEDIUM: { bg: "#fff8e1", fg: "#8a6c00", border: "#f1c21b", label: "MED" },
  LOW:    { bg: "#defbe6", fg: "#0e6027", border: "#a7f0ba", label: "LOW" },
};

function BandPill({ band }) {
  const s = BAND_STYLE[band] || BAND_STYLE.LOW;
  return (
    <span
      className="ml-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border inline-block"
      style={{ background: s.bg, color: s.fg, borderColor: s.border }}
      data-testid={`heatmap-band-${band}`}
    >
      {s.label}
    </span>
  );
}

function TotalsBar({ totals }) {
  // Iter 35.b — surface enhancement aggregate only when SMOD/CMOD data is present.
  const showEnh = (totals?.enhancements || 0) > 0;
  return (
    <div
      className={`grid grid-cols-2 ${showEnh ? "md:grid-cols-6" : "md:grid-cols-5"} gap-3`}
      data-testid="heatmap-totals"
    >
      <Card label="Objects" value={totals.objects} testId="heatmap-totals-objects" />
      <Card label="Packages" value={totals.packages} testId="heatmap-totals-packages" />
      <Card
        label="HIGH risk pkgs"
        value={totals.high_risk_packages}
        color="#a2191f"
        testId="heatmap-totals-high"
      />
      <Card
        label="MED risk pkgs"
        value={totals.medium_risk_packages}
        color="#8a6c00"
        testId="heatmap-totals-medium"
      />
      <Card
        label="LOW risk pkgs"
        value={totals.low_risk_packages}
        color="#0e6027"
        testId="heatmap-totals-low"
      />
      {showEnh && (
        <Card
          label="Active exits"
          value={(totals.enhancements_active_classic || 0) + (totals.enhancements_active_badi || 0)}
          color="#6929c4"
          testId="heatmap-totals-enhancements"
        />
      )}
    </div>
  );
}

function Card({ label, value, color, testId }) {
  return (
    <div className="ml-card p-3" data-testid={testId}>
      <div className="ml-mono text-[9px] uppercase tracking-widest text-[var(--ml-text-secondary)]">{label}</div>
      <div className="text-xl font-semibold mt-1" style={{ color: color || undefined }}>
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

function RecencyMicroBar({ recency }) {
  // Compact 5-segment bar showing where in the recency spectrum a package lives.
  const total = Object.values(recency).reduce((a, b) => a + b, 0);
  if (total === 0) return <span className="ml-mono text-[10px] text-[var(--ml-text-secondary)]">—</span>;
  const segments = [
    { key: "recent",    pct: (recency.recent / total) * 100,    color: "#0e6027", title: `${recency.recent} recent (≤30d)` },
    { key: "fresh",     pct: (recency.fresh / total) * 100,     color: "#08bdba", title: `${recency.fresh} fresh (≤90d)` },
    { key: "stale",     pct: (recency.stale / total) * 100,     color: "#f1c21b", title: `${recency.stale} stale (≤365d)` },
    { key: "year_plus", pct: (recency.year_plus / total) * 100, color: "#fa4d56", title: `${recency.year_plus} year+ (>365d)` },
    { key: "never",     pct: (recency.never / total) * 100,     color: "#c6c6c6", title: `${recency.never} never (no date)` },
  ].filter((s) => s.pct > 0);
  return (
    <div className="flex h-2 w-24 border border-[var(--ml-border)]" data-testid="heatmap-recency-bar">
      {segments.map((s) => (
        <div
          key={s.key}
          style={{ width: `${s.pct}%`, background: s.color }}
          title={s.title}
        />
      ))}
    </div>
  );
}

function ExitsCell({ enh }) {
  // Iter 35.b — compact per-package enhancement density chip.
  // Only render when there's actually SMOD/CMOD data for this package.
  if (!enh || (enh.total || 0) === 0) {
    return <span className="text-[var(--ml-text-secondary)]">—</span>;
  }
  const active = (enh.active_classic || 0) + (enh.active_badi || 0);
  return (
    <span
      className="inline-flex items-center gap-1 ml-mono text-xs"
      title={
        `${active} active (${enh.active_classic || 0} classic + ${enh.active_badi || 0} BAdI)` +
        (enh.inactive ? ` · ${enh.inactive} inactive` : "") +
        (enh.locked ? ` · ${enh.locked} locked` : "")
      }
    >
      <span className="font-semibold text-[#6929c4]">{active}</span>
      {enh.inactive > 0 && (
        <span className="text-[var(--ml-text-secondary)] text-[10px]">+{enh.inactive}i</span>
      )}
    </span>
  );
}

function PackageRow({ row }) {
  return (
    <tr
      className="border-b border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/30"
      data-testid={`heatmap-row-${row.package.replace(/[/\s()]/g, "_")}`}
    >
      <td className="px-3 py-2"><BandPill band={row.risk_band} /></td>
      <td className="px-3 py-2 ml-mono font-semibold">{row.package}</td>
      <td className="px-3 py-2 ml-mono text-right">{row.risk_score.toLocaleString()}</td>
      <td className="px-3 py-2 ml-mono text-right">{row.object_count}</td>
      <td className="px-3 py-2 ml-mono text-right text-xs">
        {row.custom_count}
        {row.modified_standard_count > 0 && (
          <span
            className="ml-1 inline-flex items-center gap-0.5 text-[#a2191f] font-semibold"
            title={`${row.modified_standard_count} modified standard SAP object(s)`}
          >
            <WarningOctagon size={10} weight="bold" /> {row.modified_standard_count}
          </span>
        )}
      </td>
      <td className="px-3 py-2 ml-mono text-right text-xs">{row.total_loc.toLocaleString()}</td>
      <td className="px-3 py-2 text-right" data-testid={`heatmap-exits-${row.package.replace(/[/\s()]/g, "_")}`}>
        <ExitsCell enh={row.enhancements} />
      </td>
      <td className="px-3 py-2"><RecencyMicroBar recency={row.recency} /></td>
      <td className="px-3 py-2 ml-mono text-xs">
        {row.top_objects.slice(0, 2).map((o) => o.obj_name).join(", ") || "—"}
      </td>
    </tr>
  );
}

export default function AbapHeatmapPanel({ workspaceId, canWrite = true }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  // Iter 31 — Share modal + shares-list refresh trigger
  const [shareOpen, setShareOpen] = useState(false);
  const [sharesRefreshKey, setSharesRefreshKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getAbapHeatmapData(workspaceId)
      .then(setData)
      .catch((e) => setError(formatApiErrorDetail(e) || "Failed to load heat map"))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await generateAbapHeatmap(workspaceId, {
        title: "SAP ABAP Custom-Code Heat Map",
      });
      toast.success("AI heat map deliverable queued — Architect persona, Claude Sonnet 4.5.");
      navigate(`/deliverables?open=${res.deliverable_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to queue heat map");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="heatmap-loading">
        <Spinner size={14} className="animate-spin inline mr-2" /> Computing heat map…
      </div>
    );
  }
  if (error) {
    return <div className="ml-card p-4 text-sm text-[#a2191f]" data-testid="heatmap-error">{error}</div>;
  }
  if (!data || data.totals.objects === 0) {
    return (
      <div className="ml-card p-8 text-center" data-testid="heatmap-empty">
        <Fire size={32} weight="duotone" className="text-[var(--ml-text-secondary)] inline mb-2" />
        <h4 className="font-semibold mb-1">Heat map needs ABAP inventory</h4>
        <p className="text-sm text-[var(--ml-text-secondary)] max-w-md mx-auto">
          Import a TADIR extract first, then return here to see the deterministic
          per-package transition-risk rollup.
        </p>
      </div>
    );
  }

  const { totals, packages, top_modified_standard } = data;

  return (
    <div className="space-y-5" data-testid="heatmap-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
            <Fire size={11} weight="bold" /> Iter 29 · Custom-code heat map
          </div>
          <h3 className="text-xl font-semibold">Transition-priority view</h3>
          <p className="text-sm text-[var(--ml-text-secondary)] max-w-2xl mt-1">
            Deterministic per-package risk scoring (modified-standard × 100 + custom LOC ÷ 100
            + recency weight). Generate the AI-narrated version to get refactor recommendations
            per package + a 3-phase transition path.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              data-testid="heatmap-share-btn"
              className="ml-btn-secondary whitespace-nowrap inline-flex items-center gap-1.5"
              title="Mint a public read-only share link"
            >
              <ShareNetwork size={14} weight="bold" /> Share
            </button>
          )}
          <a
            href={abapHeatmapPdfUrl(workspaceId)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="heatmap-pdf-btn"
            className="ml-btn-secondary whitespace-nowrap inline-flex items-center gap-1.5"
            title="Download a branded PDF of this heat map (rollup-only when no AI report has been generated)."
          >
            <Download size={14} weight="bold" /> PDF
          </a>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating || !canWrite}
            data-testid="heatmap-generate-btn"
            className="ml-btn-primary whitespace-nowrap"
            title={canWrite ? undefined : "Editor or owner role required"}
          >
            {generating ? (
              <><Spinner size={14} weight="bold" className="animate-spin" /> Queuing…</>
            ) : (
              <><Sparkle size={14} weight="bold" /> Generate AI report</>
            )}
          </button>
        </div>
      </div>

      <TotalsBar totals={totals} />

      <div className="ml-card overflow-x-auto" data-testid="heatmap-table">
        <table className="w-full text-sm">
          <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
            <tr>
              <th className="px-3 py-2 text-left">Band</th>
              <th className="px-3 py-2 text-left">Package</th>
              <th className="px-3 py-2 text-right">Score</th>
              <th className="px-3 py-2 text-right">Objects</th>
              <th className="px-3 py-2 text-right">Custom / Mod-std</th>
              <th className="px-3 py-2 text-right">LOC</th>
              <th className="px-3 py-2 text-right">Exits</th>
              <th className="px-3 py-2 text-left">Recency</th>
              <th className="px-3 py-2 text-left">Top objects</th>
            </tr>
          </thead>
          <tbody>
            {packages.map((row) => <PackageRow key={row.package} row={row} />)}
          </tbody>
        </table>
      </div>

      {top_modified_standard.length > 0 && (
        <div className="ml-card p-4" data-testid="heatmap-modified-standard">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
            <WarningOctagon size={11} weight="bold" /> Modified-standard hot spots ({top_modified_standard.length})
          </div>
          <p className="text-xs text-[var(--ml-text-secondary)] mb-3 max-w-2xl">
            These are SAP-delivered objects that have been modified in place — each one is a
            potential S/4HANA conversion blocker (SPDD / SPAU pain).
          </p>
          <div className="space-y-1.5">
            {top_modified_standard.map((o) => (
              <div
                key={`${o.obj_type}_${o.obj_name}`}
                className="flex flex-wrap items-center gap-x-3 text-sm border-l-2 pl-3 py-1"
                style={{ borderColor: "#fa4d56" }}
                data-testid={`heatmap-mod-${o.obj_name}`}
              >
                <span className="ml-mono font-semibold">{o.obj_type} {o.obj_name}</span>
                <span className="ml-mono text-xs text-[var(--ml-text-secondary)]">
                  pkg={o.package || "—"} · {(o.loc || 0).toLocaleString()} LOC
                </span>
                {o.last_changed_by && (
                  <span className="ml-mono text-xs">
                    last changed by <strong>{o.last_changed_by}</strong>
                    {o.last_changed_at ? ` · ${o.last_changed_at.slice(0, 10)}` : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ml-card p-4" data-testid="heatmap-methodology">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
          <ChartBar size={11} weight="bold" /> How the risk score works
        </div>
        <div className="text-sm space-y-1.5">
          <p>
            <strong>Score</strong> = <code className="ml-mono text-xs bg-[var(--ml-surface)]/60 px-1">modified_std × 100  +  loc ÷ 100  +  Σ recency_weight  +  enhancement_weight</code>
          </p>
          <p className="text-xs text-[var(--ml-text-secondary)]">
            Recency weights per object: <strong>recent</strong> (≤30d) = 30,
            <strong> fresh</strong> (≤90d) = 20, <strong>stale</strong> (≤365d) = 10,
            <strong> year+</strong> (&gt;365d) = 5, <strong>never</strong> (no date) = 1.
          </p>
          <p className="text-xs text-[var(--ml-text-secondary)]">
            Enhancement weights (Iter 35.b): active <strong>function/screen/menu/field</strong> exits = 25 each
            (CMOD-deprecated in NetWeaver 7.5+ → S/4 refactor cost), active <strong>BAdIs</strong> = 8 each
            (already modern → light port), inactive / locked = 0.
          </p>
          <p className="text-xs text-[var(--ml-text-secondary)]">
            <strong>HIGH</strong> band: any modified-standard OR score ≥ 200.
            <strong> MEDIUM</strong>: score ≥ 60 OR LOC ≥ 5,000.
            <strong> LOW</strong>: everything else.
          </p>
        </div>
      </div>

      <div className="ml-card p-4 bg-[var(--ml-surface)]/30" data-testid="heatmap-ai-cta">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="font-semibold flex items-center gap-1.5 mb-0.5">
              <Sparkle size={14} weight="duotone" /> Want refactor recommendations per package?
            </div>
            <p className="text-xs text-[var(--ml-text-secondary)]">
              Claude Sonnet 4.5 will analyse this rollup and produce an Architect-persona briefing
              with risk methodology, package deep-dives, modified-standard hot-spot triage, and a
              three-phase transition plan. Result lands in your Deliverables.
            </p>
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating || !canWrite}
            data-testid="heatmap-generate-btn-bottom"
            className="ml-btn-primary"
          >
            {generating ? "Queuing…" : <><Sparkle size={14} weight="bold" /> Generate AI report <ArrowSquareOut size={12} weight="bold" /></>}
          </button>
        </div>
      </div>

      {/* Iter 31 — Active share links list. Always rendered (even with zero
          shares) so consultants discover the feature. */}
      <HeatmapSharesList
        workspaceId={workspaceId}
        canWrite={canWrite}
        refreshKey={sharesRefreshKey}
      />

      {/* Iter 31 — Share modal. Controlled. */}
      <HeatmapShareModal
        workspaceId={workspaceId}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        onCreated={() => setSharesRefreshKey((k) => k + 1)}
      />
    </div>
  );
}
